/**
 * Booking Type → Booking Link section.
 *
 * Renders the public booking URL(s) for a booking type so an org member can
 * copy/share them without leaving the slideout. Supports two URL formats:
 *
 *   • Public URL (globally unique slug):
 *       /<global_slug>/
 *   • Short URL (auto-generated short code):
 *       /m/<short_code>/
 *
 * Both are fetched from
 * ``GET /api/orgs/<org_id>/scheduling/booking-types/<entity_id>/booking-link/``.
 * If the row is missing (legacy booking types created before the short-code
 * post-commit callback) this section silently omits those URLs.
 */

import { Copy01, Edit01, LinkExternal01 } from '@untitledui/icons';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { isApiError } from '@/lib/errors';
import {
  buildGlobalSlugUrl,
  buildShortCodeUrl,
  stripProtocol,
} from '@/lib/scheduling/buildBookingUrl';
import { SlugAvailabilityField } from '@/lib/scheduling/SlugAvailabilityField';
import {
  useBookingLink,
  useUpdateBookingLinkSlug,
} from '@/lib/scheduling/useBookingLink';
import type { SlugStatus } from '@/lib/scheduling/useSlugAvailability';
import type { CrmObject } from '@/types/crm';

import { CollapsibleSection } from './collapsible-section';

interface BookingLinkSectionProps {
  object: CrmObject;
}

export function BookingLinkSection({ object }: BookingLinkSectionProps) {
  // Fetch booking-link data through React Query so it is cached across
  // slideout remounts. The section previously used a raw apiGet + useState,
  // which reset to empty every time the slideout was reopened via
  // back-navigation (and flashed in late on every open). A 404 is expected for
  // legacy booking types with no short-code row and resolves to `undefined`.
  const { data } = useBookingLink(object.org_id, object.entity_id);
  const shortCode = data?.short_code ?? null;
  const globalSlug = data?.global_slug ?? null;
  const shareBaseUrl = data?.share_base_url ?? null;

  const updateSlug = useUpdateBookingLinkSlug(object.org_id, object.entity_id);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [draftSlug, setDraftSlug] = useState('');
  const [draftStatus, setDraftStatus] = useState<SlugStatus>('idle');

  // Use the configured share-base URL if the API gave us one, otherwise fall
  // back to the current dashboard origin so the link still resolves.
  const baseUrl = shareBaseUrl ?? window.location.origin;

  const globalUrl = buildGlobalSlugUrl(baseUrl, globalSlug);
  const shortUrl = buildShortCodeUrl(baseUrl, shortCode);

  const handleCopy = useCallback((url: string) => {
    if (!navigator.clipboard?.writeText) {
      toast.error('Clipboard not available');
      return;
    }
    navigator.clipboard.writeText(url).then(
      () => toast.success('Booking link copied'),
      () => toast.error("We couldn't copy the booking link. Try again.")
    );
  }, []);

  const handleOpenEdit = useCallback(() => {
    setDraftSlug(globalSlug ?? '');
    setDraftStatus('idle');
    setIsEditOpen(true);
  }, [globalSlug]);

  const handleCancelEdit = useCallback(() => setIsEditOpen(false), []);

  const handleDraftChange = useCallback(
    (next: string) => setDraftSlug(next),
    []
  );

  const handleDraftStatusChange = useCallback(
    (s: SlugStatus) => setDraftStatus(s),
    []
  );

  const handleConfirmEdit = useCallback(async () => {
    if (object.org_id == null || object.entity_id == null) {
      return;
    }
    try {
      // The mutation updates the booking-link query cache on success, so the
      // section reflects the new slug immediately without a refetch.
      await updateSlug.mutateAsync(draftSlug);
      toast.success('URL slug updated');
      setIsEditOpen(false);
    } catch (err: unknown) {
      if (isApiError(err) && err.statusCode === 409) {
        console.warn('Slug collision on booking type update', {
          entityId: object.entity_id,
          orgId: object.org_id,
        });
        toast.error(
          'This URL slug is already taken. Please choose a different one.'
        );
      } else {
        toast.error("We couldn't update the URL slug. Try again.");
      }
    }
  }, [draftSlug, object.org_id, object.entity_id, updateSlug]);

  if (!shortUrl && !globalUrl) {
    return null;
  }

  const domainPrefix = `${stripProtocol(baseUrl)}/`;

  return (
    <CollapsibleSection title='Booking link' defaultExpanded>
      <div className='space-y-3'>
        {globalUrl && (
          <BookingLinkRow
            label='Public URL'
            url={globalUrl}
            onCopy={handleCopy}
            onEdit={handleOpenEdit}
          />
        )}
        {shortUrl && (
          <BookingLinkRow
            label='Short URL'
            url={shortUrl}
            onCopy={handleCopy}
          />
        )}
      </div>
      <ConfirmationDialog
        isOpen={isEditOpen}
        title='Edit URL slug'
        description='Changing this URL will break any links you have shared. Are you sure?'
        confirmLabel='Update slug'
        variant='warning'
        isLoading={updateSlug.isPending}
        isConfirmDisabled={draftStatus !== 'available'}
        onConfirm={handleConfirmEdit}
        onCancel={handleCancelEdit}
      >
        <SlugAvailabilityField
          value={draftSlug}
          onChange={handleDraftChange}
          onStatusChange={handleDraftStatusChange}
          domainPrefix={domainPrefix}
          aria-label='New public URL slug'
          idleEmptyMessage='Enter a new URL slug.'
        />
      </ConfirmationDialog>
    </CollapsibleSection>
  );
}

interface BookingLinkRowProps {
  label: string;
  url: string;
  onCopy: (url: string) => void;
  onEdit?: () => void;
}

function BookingLinkRow({ label, url, onCopy, onEdit }: BookingLinkRowProps) {
  const handleCopyClick = useCallback(() => onCopy(url), [onCopy, url]);

  return (
    <div className='flex items-center gap-2'>
      <span className='text-xs font-medium text-[var(--color-text-secondary)] w-[70px] shrink-0'>
        {label}
      </span>
      <a
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        className='flex-1 truncate text-sm text-fg-brand-primary hover:underline'
      >
        {stripProtocol(url)}
      </a>
      {onEdit && (
        <Button
          color='tertiary'
          size='sm'
          iconLeading={Edit01}
          onClick={onEdit}
          title='Edit URL slug'
          aria-label='Edit URL slug'
        />
      )}
      <Button
        color='tertiary'
        size='sm'
        iconLeading={Copy01}
        onClick={handleCopyClick}
        title='Copy link'
        aria-label='Copy link'
      />
      <a
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        title='Open link'
        aria-label='Open link'
        className='p-1.5 rounded hover:bg-[var(--color-bg-tertiary)]'
      >
        <LinkExternal01 className='h-4 w-4 text-[var(--color-text-secondary)]' />
      </a>
    </div>
  );
}
